/****************************************************
 * Module Name:  TenderLot.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import { Box, Tooltip, Typography } from "@mui/material";
import React, { useState, useEffect } from "react";
import { DataGridPro, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarExport, GridToolbarFilterButton, GridToolbarQuickFilter } from "@mui/x-data-grid-pro";
import CircularProgress from "@mui/material/CircularProgress";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import Select from "react-select";
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import { LoadingButton } from "@mui/lab";
import Constants from "../../../utility/constants";
import TenderLotProxy from "../../../utility/proxy/manager/tender/tenderLotProxy";
import { BiSearchAlt2 } from "react-icons/bi";
import TabMenuSnackbar from "../../common/tabMenuSnackbar";
import renderCellExpand from "../../common/dataGridCellExpand";
import Utility from "../../../utility/utility";
import NoPermission from "../../common/noPermission";
import Papa from 'papaparse';
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { BackupTable } from "@mui/icons-material";
import PageTitle from "../../common/pageTitle";

const TenderLot = () => {

    const local_Permission = Constants.USER_PERMISSION.filter((iPermission) =>
        iPermission.permissionid === Constants.PERMISSIONS.TENDER_OPERATION_TENDER_LOT);

    const [companyTender, setCompanyTender] = useState("");
    const [companyTenderOptions, setCompanyTenderOptions] = useState([]);
    const [tenderLotLoader, setTenderLotLoader] = useState(false);
    const [tenderLotList, setTenderLotList] = useState([]);
    const [uploadLoader, setUploadLoader] = useState(false);
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarVariant, setSnackbarVariant] = useState("");
    const [selectionModel, setSelectionModel] = useState([]);
    const [CompanyDataLoader, setCompanyDataLoader] = useState(true);
    const [columnNames, setColumnNames] = React.useState([]);

    useEffect(() => {
        PageTitle("TENDER_OPERATION_TENDER_LOT");
        checkPermission();
    }, []);

    const checkPermission = () => {
        if (Utility.isReadAllowed(local_Permission)) {
            getCompanyTender();
        }
    }

    const getCompanyTender = () => {

        setTenderLotList([]);
        setCompanyDataLoader(true);
        setCompanyTenderOptions([]);
        setCompanyTender("");
        let optionsArray = [];
        TenderLotProxy.GetAllCompanyTendersToEvaluate((response) => {
            response.map((data) => {
                let option = { value: data.tenderid, label: data.companyname + " | " + (data.tendername === null ? " - " : data.tendername), companyid: data.companyid };
                optionsArray.push(option);
                return true;
            });
            setCompanyDataLoader(false);
        })
        setCompanyTenderOptions(optionsArray);
    }

    const handleSearch = () => {
        setTenderLotLoader(true);
        // need to fetch such companies and tenders which are not yet locked 
        // so both stone wise bid and lot wise bid status should be "IN_PROGRESS"
        setTenderLotList([]);
        TenderLotProxy.TenderLotDataByCompany(companyTender.value, companyTender.companyid, false, "IN_PROGRESS", "IN_PROGRESS", (response) => {
            if (response) {
                const uniqueFilterValues = {
                    minename: Utility.getDistinctOptions(response, "minename"),
                    tendername: Utility.getDistinctOptions(response, "tendername"),
                };
                prepareColumns(uniqueFilterValues)
                setTenderLotList(response);
                setTenderLotLoader(false);
            } else {
                setTenderLotLoader(false);
            }
        });
    };

    const prepareColumns = (uniqueFilterValues) => {
        const columnNames = columnsWithoutPermission.map(woPermissionColumn => {
            let guids = [];

            // Determine which GUIDs (permissions) should be added based on the column field
            switch (woPermissionColumn.field) {
                case "tenderlotid":
                    guids = [Constants.COLUMN_PERMISSION.MFG_LOT_LOTCODE];
                    break;
                case "mineid":
                    guids = [Constants.COLUMN_PERMISSION.MFG_MINE_NAME];
                    break;
                case "pcs":
                    guids = [
                        Constants.COLUMN_PERMISSION.MFG_LOT_BASIC_PRICE,
                        Constants.COLUMN_PERMISSION.MFG_LOT_BASIC_AMOUNT
                    ];
                    break;
                default:
                    guids = [];
            }

            // Retrieve valid permission columns
            const permissionColumns = guids
                .map(id => Constants.USER_COLUMN_PERMISSION.find(permission => permission === id)) // Match permissions
                .filter(Boolean) // Remove undefined values (invalid permissions)
                .map(guid => {
                    const columnData = columnsWithPermission[guid]; // Get column details

                    // If the columnData is missing or an empty object, skip adding it
                    if (!columnData || Object.keys(columnData).length === 0) {
                        return null;
                    }

                    // Retrieve unique filter values for the column, defaulting to an empty array
                    const filterData = uniqueFilterValues[columnData.field] || [];

                    return {
                        ...columnData,  // Copy all existing column properties
                        valueOptions: filterData,  // Assign unique filter values
                    };
                })
                .filter(Boolean); // Remove any null values from the list

            // Modify the original column before adding it to the list
            const modifiedColumn = {
                ...woPermissionColumn,  // Retain original column properties
                valueOptions: uniqueFilterValues[woPermissionColumn.field] || [], // Assign unique filter values
            };

            // Return an array with the modified column and any valid permission columns
            return [modifiedColumn, ...permissionColumns];
        }).flat(); // Flatten the array


        // Update state with the prepared columns
        setColumnNames(columnNames);
    };

    const columnsWithPermission = {
        "eabe76e1-e41b-40ef-b9ae-cbd13293bd1e": {
            field: "lotcode",
            headerName: "LOT CODE",
            headerAlign: "center",
            align: "center",
            width: 78,
            renderCell: renderCellExpand,
            filterOperators: Utility.defaultFilters(),
            sortComparator: (v1, v2) => Utility.sortAlphaNumeric(v1, v2)
        },
        "4a1de405-1802-4664-85e2-6ff7cf8c3bba": {
            field: "minename",
            headerName: "MINE NAME",
            headerAlign: "center",
            align: "center",
            width: 120,
            type: "singleSelect",
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        "3634473f-45cf-4def-8e69-c1f34ebe0f18": {
            field: "basicprice",
            headerName: "BASIC PRICE",
            headerAlign: "center",
            align: "center",
            width: 110,
            type: "number",
            valueFormatter: (params) => params.value ? (params.value).toFixed(2) : 0,
            renderCell: (params) => params.value ? (params.value).toFixed(2) : 0,
        },
        "394908cf-dc48-40ee-b68f-23efa651806e": {
            field: "basicamount",
            headerName: "BASIC AMOUNT",
            headerAlign: "center",
            align: "center",
            width: 130,
            type: "number",
            valueFormatter: (params) => params.value ? (params.value).toFixed(2) : 0,
            renderCell: (params) => params.value ? (params.value).toFixed(2) : 0,
        },
    };

    const columnsWithoutPermission = [
        {
            field: "tenderlotid",
            headerName: "TENDER LOT ID",
            headerAlign: "center",
            align: "center",
            width: 120,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "mineid",
            headerName: "MINE ID",
            headerAlign: "center",
            align: "center",
            width: 100,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "tenderid",
            headerName: "TENDER ID",
            headerAlign: "center",
            align: "center",
            width: 100,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "tendername",
            headerName: "TENDER",
            headerAlign: "center",
            align: "center",
            width: 250,
            type: "singleSelect",
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "size",
            headerName: "SIZE",
            headerAlign: "center",
            align: "center",
            width: 76,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "description",
            headerName: "DESCRIPTION",
            headerAlign: "center",
            align: "center",
            width: 212,
            renderCell: renderCellExpand,
        },
        {
            field: "remark",
            headerName: "REMARK",
            headerAlign: "center",
            align: "center",
            width: 200,
            renderCell: renderCellExpand,
        },
        {
            field: "pcs",
            headerName: "PCS",
            headerAlign: "center",
            align: "center",
            width: 50,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "carats",
            headerName: "CARATS",
            headerAlign: "center",
            align: "center",
            width: 76,
            type: "number",
            valueFormatter: (params) => (params.value).toFixed(2),
            renderCell: (params) => (params.value).toFixed(2),
        },
        {
            field: "evaluate",
            headerName: "EVALUATE",
            headerAlign: "center",
            align: "center",
            width: 100,
            type: "boolean",
        },
    ];

    const evaluateTenderLots = () => {
        if (selectionModel.length > 0) {
            const selectedItems = selectionModel
                .map(id => tenderLotList.find(i => i.tenderlotid === id))
                .map(({ tenderlotid, carats, pcs }) => ({ tenderlotid, size: parseFloat(carats), stonesizepercent: 100, pcs }));

            setTenderLotLoader(true);
            TenderLotProxy.EvaluateTenderLots(selectedItems, (response) => {
                if (response.status === "Success") {
                    setTenderLotLoader(false);
                    setShowSnackbar(true);
                    setSnackbarMessage(response.errorMessage);
                    setSnackbarVariant("success");
                    handleSearch();
                } else {
                    setShowSnackbar(true);
                    setSnackbarMessage(Constants.APiFailErrorMessage);
                    setSnackbarVariant("error");
                }
            });
        }
    }

    const handleUpload = (file) => {
        const reader = new FileReader();
        reader.onload = () => {
            const text = reader.result;
            Papa.parse(text, {
                header: true,
                skipEmptyLines: true,
                complete: (result) => {
                    if (result.data.length <= 0) {
                        setShowSnackbar(true);
                        setSnackbarVariant("warning");
                        setSnackbarMessage("No Tender Stones Found");
                        setUploadLoader(false);
                        return;
                    }

                    let headersFromCSV = Object.keys(result.data[0]);
                    let requiredColumns = ['mineid', 'lotcode', 'size', 'pcs', 'carats', 'basic price', 'basic amount', 'description', 'remark'];
                    if (requiredColumns.length === headersFromCSV.length) {
                        let data = [];
                        let flag = true;
                        result.data.forEach(record => {
                            if (flag && record['mineid'] !== undefined && record['lotcode'] !== undefined && parseInt(record['mineid']) && companyTender && companyTender.value &&
                                record['size'] !== undefined && record['carats'] !== undefined && parseFloat(record['carats'])) {
                                let iRow = {
                                    mineid: parseInt(record['mineid']),
                                    lotcode: record['lotcode'],
                                    tenderid: companyTender.value,
                                    size: record['size'],
                                    carats: parseFloat(record['carats']),
                                    description: record['description'],
                                    pcs: record['pcs'],
                                    remark: record['remark'],
                                    basicprice: parseFloat(record['basic price']),
                                    basicamount: parseFloat(record['basic amount']),
                                    evaluate: record['evaluate']
                                }
                                data.push(iRow);
                            } else {
                                flag = false;
                            }
                            return true;
                        });

                        if (flag) {
                            TenderLotProxy.UploadTenderLot(data, (response) => {
                                if (response.status === "Success") {
                                    setShowSnackbar(true);
                                    setSnackbarMessage(response.errorMessage);
                                    setSnackbarVariant("success");
                                    setUploadLoader(false);
                                    handleSearch();
                                } else {
                                    setShowSnackbar(true);
                                    setSnackbarMessage(response.status === "Error" ? response.errorMessage : Constants.APiFailErrorMessage);
                                    setSnackbarVariant("error");
                                    setUploadLoader(false);
                                }
                            });
                        } else {
                            setShowSnackbar(true);
                            setSnackbarMessage("Please check the data and try again later.");
                            setSnackbarVariant("error");
                            setUploadLoader(false);
                        }
                    } else {
                        console.error('CSV columns do not match table columns.');
                        setShowSnackbar(true);
                        setSnackbarMessage("CSV columns do not match table columns.");
                        setSnackbarVariant("error");
                    }
                },
                error: (error) => {
                    console.error('Error parsing CSV:', error);
                    setShowSnackbar(true);
                    setSnackbarVariant("error");
                    setSnackbarMessage('Error parsing CSV:', error);
                }
            });
        };

        reader.readAsText(file);
    };


    const handleClear = () => {
        const fileInput = document.getElementById('file-input');
        fileInput.value = null;
    }

    const CustomToolbar = () => {
        return (
            <GridToolbarContainer
                sx={{
                    display: "flex",
                    alignItems: "center",
                    marginHorizontal: "5px",
                    "&.MuiDataGrid-toolbarContainer": {
                        padding: "0px 4px !important",
                    },
                }}
            >
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <GridToolbarExport />
                <Box sx={{ flexGrow: 1, display: { md: "none", lg: "block" } }} />
                {tenderLotLoader ? (
                    <CircularProgress size="1rem" />
                ) : (
                    <Tooltip title="Reload List">
                        <RefreshOutlinedIcon
                            color="primary"
                            fontSize="medium"
                            onClick={() => handleSearch()}
                            sx={{ cursor: "pointer" }}
                        />
                    </Tooltip>
                )}
                <LoadingButton
                    variant="contained"
                    color="info"
                    onClick={(e) => evaluateTenderLots()}
                    loading={tenderLotLoader}
                    loadingPosition="start"
                    sx={{
                        padding: "3px 12px",
                    }}
                    startIcon={<AssignmentTurnedInIcon />}
                    type="Submit"
                    disabled={selectionModel.length === 0}
                >
                    Evaluate
                </LoadingButton>
                <LoadingButton
                    color="info"
                    variant="outlined"
                    size="small"
                    component="label"
                    loading={uploadLoader}
                    loadingPosition="start"
                    startIcon={<UploadFileIcon size="large" />}
                    disabled={!companyTender}
                >
                    Upload
                    <input
                        id="file-input"
                        hidden
                        accept=".csv"
                        type="file"
                        onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                                setUploadLoader(true);
                                handleUpload(file);
                            }
                        }}
                        onClick={(e) => {
                            handleClear()
                        }}
                    />
                </LoadingButton>
                <a
                    href="/demo_excel/CompanyTenderLotUpload.csv"
                    download="CompanyTenderLotUpload.csv"
                    style={{ color: "red" }}
                >
                    <BackupTable size={25} />
                </a>
                <GridToolbarQuickFilter debounceMs={500} />
            </GridToolbarContainer>
        )
    }


    return !Utility.isReadAllowed(local_Permission) ? <NoPermission /> : (
        <Box>

            {showSnackbar ? (
                <TabMenuSnackbar
                    show={true}
                    message={snackbarMessage}
                    variant={snackbarVariant}
                    autoHide={Constants.SnackBarCloseTimer}
                    title={snackbarVariant === "success" ? "Success" :
                        snackbarVariant === "error" ? "Failed" : "Warning"}
                    handleClose={() => setShowSnackbar(false)}
                />
            ) : null}

            <Box
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: "flex-start",
                    width: "100%",
                    marginBottom: 1,
                    gap: "15px",
                    marginTop: "10px",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: "15px",
                        flex: 1,
                        minWidth: 0,
                    }}
                >
                    <Typography sx={{ whiteSpace: "nowrap" }}>Company Tender</Typography>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: "15px",
                        }}
                    >
                        <Box
                            sx={{
                                width: { xs: "100%", sm: "25rem" },
                                minWidth: "200px",
                            }}
                        >
                            <Select menuPlacement="auto"
                                blurInputOnSelect={false}
                                menuPortalTarget={document.body}
                                styles={{
                                    ...Constants.Styles,
                                }}
                                getOptionLabel={(option) => option.label}
                                getOptionValue={(option) => option.value}
                                options={companyTenderOptions}
                                value={companyTender}
                                isClearable={true}
                                onChange={(e) => setCompanyTender(e)}
                            />
                        </Box>

                        {CompanyDataLoader ?
                            (<Box>
                                {<CircularProgress size={20} />}
                            </Box>) :
                            (<Box>
                                <Tooltip title="Reload List">
                                    <RefreshOutlinedIcon
                                        onClick={() => getCompanyTender()}
                                        color="primary"
                                        fontSize="medium"
                                        sx={{ cursor: "pointer" }}
                                    />
                                </Tooltip>
                            </Box>)}

                        <Box
                            sx={{
                                "& .Mui-disabled": {
                                    backgroundColor: "rgba(0, 0, 0, 0.12) !important",
                                    color: "rgba(0, 0, 0, 0.26) !important",
                                },
                            }}
                        >
                            <LoadingButton
                                sx={{ mr: 1 }}
                                variant="contained"
                                color="info"
                                onClick={handleSearch}
                                loading={tenderLotLoader}
                                loadingPosition="start"
                                startIcon={<BiSearchAlt2 />}
                                type="Submit"
                                disabled={!companyTender}
                            >
                                Search
                            </LoadingButton>
                        </Box>
                    </Box>
                </Box>
            </Box>
            <Box
                sx={{
                    backgroundColor: '#fff',
                    color: '#000',
                    width: "100%",
                    position: "relative",
                    "& .MuiTablePagination-selectLabel": {
                        marginTop: "15px",
                    },
                    "& .MuiTablePagination-displayedRows": {
                        marginTop: "15px",
                    },
                    "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                        fontWeight: Constants.defaultFontWeight,
                    },
                    "& .css-cc8tf1": {
                        fontWeight: Constants.defaultFontWeight,
                    },
                    height: "79vh",
                    "& .lavenderCell": {
                        color: "#080808",
                        backgroundColor: "#D8BFD8",
                    },
                    "& .MuiTablePagination-toolbar, .MuiDataGrid-footerContainer": {
                        minHeight: "30px"
                    },
                    "& .MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                        margin: 0
                    },
                    marginTop: "5px"
                }}
            >
                <DataGridPro
                    disableRowSelectionOnClick={true}
                    density="compact"
                    loading={tenderLotLoader}
                    sx={{
                        '& .MuiDataGrid-columnHeader': {
                            color: '#fff',
                            backgroundColor: 'grey',
                        },
                        "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                            py: "4px",
                        },
                        "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                            py: "12px",
                        },
                        "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                            py: "18px",
                        },

                        fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                    }}
                    slots={{ toolbar: CustomToolbar }}
                    rows={tenderLotList}
                    columns={columnNames}
                    getRowId={(row) => row.tenderlotid}
                    checkboxSelection={tenderLotList.length > 0}
                    onRowSelectionModelChange={(newSelectionModel) => setSelectionModel(newSelectionModel)}
                    rowSelectionModel={selectionModel}
                    rowSpacingType="border"
                    initialState={{
                        columns: {
                            columnVisibilityModel: {
                                // Hide columns status and traderName, the other columns will remain visible
                                id: false,
                            },
                        },
                        pagination: {
                            paginationModel: Constants.pageSize,
                        },
                    }}
                    pageSizeOptions={Constants.pageSizeOptions}
                    pagination
                    slotProps={{
                        toolbar: {
                            showQuickFilter: true,
                            quickFilterProps: { debounceMs: 500 },
                        },
                    }}
                />
            </Box>

        </Box>
    )
}
export default TenderLot;
